import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../../i18n';
import { BottleCap } from '../app/BottleCap';
import type { Comment, CommentThread } from '../../lib/comments';

/**
 * A chat source abstracts where comments live (a match or a league) so the
 * same UI drives both. `key` re-loads the thread when the target changes.
 */
export type ChatSource = {
  key: string | number;
  list: () => Promise<CommentThread>;
  post: (body: string) => Promise<Comment>;
  remove: (id: number) => Promise<void>;
};

/** Flat chat list + composer. Honors the thread's `enabled` flag. */
export function ChatPanel({ source }: { source: ChatSource }) {
  const { t, localePath } = useI18n();
  const [comments, setComments] = useState<Comment[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [disabledMsg, setDisabledMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    source
      .list()
      .then((th) => {
        if (!alive) return;
        setComments(th.comments);
        setEnabled(th.enabled);
        setDisabledMsg(th.disabledMessage ?? '');
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [source.key]);

  async function send() {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const c = await source.post(body);
      setComments((prev) => [...prev, c]);
      setDraft('');
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  }
  async function remove(id: number) {
    try {
      await source.remove(id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch {}
  }

  if (loading) return <div class="bc-spinner">{t('app.loading')}</div>;
  if (!enabled) return <p class="bc-empty">{disabledMsg || t('game.chat_disabled')}</p>;

  return (
    <div class="chat">
      <div class="chat__list">
        {comments.length === 0 && <p class="bc-empty">{t('game.chat_empty')}</p>}
        {comments.map((c) => (
          <div key={c.id} class={`chat__msg${c.isMine ? ' is-mine' : ''}`}>
            {c.avatarUrl ? (
              <img class="chat__avatar" src={c.avatarUrl} alt="" />
            ) : (
              <BottleCap code={c.avatarCap} size={28} />
            )}
            <div class="chat__bubble">
              {c.isMine ? (
                <span class="chat__name">{c.nickname}</span>
              ) : (
                <a class="chat__name chat__name--link" href={localePath(`/u/${c.userId}`)}>
                  {c.nickname}
                </a>
              )}
              <span class="chat__body">{c.body}</span>
            </div>
            {c.isMine && (
              <button class="chat__del" aria-label="×" onClick={() => remove(c.id)}>×</button>
            )}
          </div>
        ))}
      </div>
      <div class="chat__compose">
        <input
          class="bc-input"
          placeholder={t('game.chat_placeholder')}
          value={draft}
          maxLength={500}
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
        />
        <button class="bc-pill" disabled={!draft.trim() || busy} onClick={send}>{t('game.chat_send')}</button>
      </div>
    </div>
  );
}
